/**
 * DAO化の申請の確認・承認・却下（運営だけ。BOARD A-34、2026-10-21）
 * 申請が来ると運営にメールが届き、そのリンクからこの画面を開く（本人確認の照合 /admin/kyc-verification と同じ形）。
 * 承認・却下の API は管理者のアカウントでなければ 403 を返す
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const STATUS_LABELS = {
  pending: '審査中',
  approved: '承認済み',
  rejected: '却下',
  withdrawn: '取り下げ',
};

export default function DaoApplicationReview() {
  const { applicationId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [application, setApplication] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [resultMessage, setResultMessage] = useState('');

  useEffect(() => {
    const fetchApplication = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await axios.get(
          `${API_URL}/api/dao/applications/${applicationId}`,
          authConfig()
        );
        setApplication(response.data.application);
      } catch (fetchError) {
        console.error('DAO申請の取得エラー:', fetchError);
        setError(fetchError.response?.data?.message || 'DAO申請を読み込めませんでした');
      } finally {
        setLoading(false);
      }
    };
    fetchApplication();
  }, [applicationId]);

  const handleDecision = async (decision) => {
    const label = decision === 'approve' ? '承認' : '却下';
    if (!window.confirm(`この申請を${label}します。よろしいですか？`)) return;
    try {
      setProcessing(true);
      setResultMessage('');
      const response = await axios.post(
        `${API_URL}/api/dao/applications/${applicationId}/${decision}`,
        { review_notes: reviewNotes || null },
        authConfig()
      );
      setResultMessage(response.data.message || `${label}しました`);
      setApplication((prev) => ({
        ...prev,
        status: decision === 'approve' ? 'approved' : 'rejected',
      }));
    } catch (decisionError) {
      console.error(`DAO申請の${label}エラー:`, decisionError);
      setResultMessage(
        decisionError.response?.status === 403
          ? '管理者のアカウントでログインしてから操作してください'
          : decisionError.response?.data?.message || `${label}できませんでした`
      );
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-900">読み込み中…</div>;
  }
  if (error) {
    return (
      <div className="p-6">
        <p className="text-red-700 font-semibold">{error}</p>
      </div>
    );
  }
  if (!application) return null;

  const status = application.status;
  const statusLabel = STATUS_LABELS[status] || status;

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4 text-gray-900">
      <h1 className="text-xl font-bold">DAO化の申請の確認</h1>

      <section className="border border-gray-400 rounded p-4 space-y-2">
        <p>
          <span className="font-semibold">DAO の名前：</span>
          {application.dao_name}
        </p>
        <p>
          <span className="font-semibold">状態：</span>
          {statusLabel}
        </p>
        <p>
          <span className="font-semibold">申請者：</span>
          {application.applicant?.display_name || '不明'}
        </p>
        {application.baseStore && (
          <p>
            <span className="font-semibold">母体のストア：</span>
            {application.baseStore.store_name || application.baseStore.name}
          </p>
        )}
        <div>
          <p className="font-semibold">設立の目的</p>
          <p className="whitespace-pre-wrap">{application.purpose}</p>
        </div>
        <div>
          <p className="font-semibold">ガバナンスの規約</p>
          <p className="whitespace-pre-wrap">{application.governance_policy}</p>
        </div>
      </section>

      {status === 'pending' ? (
        <section className="space-y-3">
          <label className="block">
            <span className="font-semibold">審査のメモ（任意）</span>
            <textarea
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              rows={3}
              className="mt-1 w-full border border-gray-500 rounded p-2"
            />
          </label>
          <div className="flex gap-3">
            <button
              onClick={() => handleDecision('approve')}
              disabled={processing}
              className="bg-green-700 text-white px-4 py-2 rounded hover:bg-green-800 disabled:bg-gray-500"
            >
              承認する
            </button>
            <button
              onClick={() => handleDecision('reject')}
              disabled={processing}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:bg-gray-500"
            >
              却下する
            </button>
          </div>
        </section>
      ) : (
        <p className="font-semibold">この申請は{statusLabel}です。</p>
      )}

      {resultMessage && <p className="font-semibold">{resultMessage}</p>}
    </div>
  );
}
